import { Link } from 'react-router-dom';

import { UnifiedIcon } from '@/components/UnifiedIcon';
import { ThemeToggle } from '@/designSystem/atoms/ThemeToggle';
import { useAppShellBreadcrumbs } from '@/layouts/useAppShellBreadcrumbs';

export interface AppShellTopBarProps {
  onOpenMobile: () => void;
}

/**
 * Top bar with mobile menu toggle and breadcrumbs.
 */
export function AppShellTopBar({ onOpenMobile }: AppShellTopBarProps) {
  const breadcrumbs = useAppShellBreadcrumbs();

  return (
    <header
      data-testid="agents-app-shell-topbar"
      className="flex items-center gap-4 border-b border-default bg-surface px-4 py-3 md:px-6"
    >
      <button
        type="button"
        aria-label="Open navigation menu"
        data-testid="agents-app-shell-menu-toggle"
        className="rounded-lg p-2 text-secondary transition-colors hover:bg-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--kc-focus-ring)] md:hidden"
        onClick={onOpenMobile}
      >
        <UnifiedIcon name="List" size={22} className="h-5 w-5" />
      </button>

      <nav
        data-testid="agents-app-shell-breadcrumbs"
        aria-label="Breadcrumb"
        className="flex min-w-0 flex-1 flex-wrap items-center gap-1 text-sm text-secondary"
      >
        {breadcrumbs.map((crumb, index) => {
          const isLast = index === breadcrumbs.length - 1;

          return (
            <span key={`${crumb.label}-${index}`} className="flex min-w-0 items-center gap-1">
              {index > 0 ? (
                <UnifiedIcon name="CaretRight" size={14} className="h-3.5 w-3.5 shrink-0 opacity-60" />
              ) : null}
              {crumb.path && !isLast ? (
                <Link to={crumb.path} className="truncate text-link hover:text-[var(--kc-link-hover)]">
                  {crumb.label}
                </Link>
              ) : (
                <span className={`truncate ${isLast ? 'font-medium text-primary' : ''}`}>{crumb.label}</span>
              )}
            </span>
          );
        })}
      </nav>

      <ThemeToggle />
    </header>
  );
}
